import { useRouter } from 'next/router';
import Link from 'next/link';
import { ArrowLeft, Mail, Globe, ScrollText, FileText, MapPin } from 'lucide-react';

const sections = [
  {
    title: '1. Acceptance of Terms',
    body: 'By accessing or using the Gyansathi website, mobile application, or any related services, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, please do not use our platform.',
  },
  {
    title: '2. About Gyansathi',
    body: 'Gyansathi is an educational platform offering academic guidance, skill development programs, online learning resources, career counseling, certification courses, and student support services to learners across India.',
  },
  {
    title: '3. Eligibility',
    body: 'Our services are intended for users who are at least 18 years of age, or younger users with the consent of a parent or legal guardian. By using the platform, you represent that you meet these eligibility requirements.',
  },
  {
    title: '4. User Accounts',
    body: 'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to provide accurate, current, and complete information during registration and to keep it updated. Gyansathi reserves the right to suspend or terminate accounts that contain false information or violate these terms.',
  },
  {
    title: '5. Use of Services',
    body: 'You agree to use Gyansathi only for lawful purposes and in accordance with these Terms. You shall not misuse the platform, attempt to gain unauthorized access, disrupt the service, upload harmful content, or use the platform in any manner that infringes the rights of others.',
  },
  {
    title: '6. Course Enrollment and Access',
    body: 'Enrollment in any course is subject to availability, applicable eligibility criteria, and payment of the prescribed fees (where applicable). Access to course materials is granted for personal, non-commercial educational use only and may be limited in duration.',
  },
  {
    title: '7. Payments, Fees, and Refunds',
    body: 'Certain courses, certifications, or services may require payment of fees. All fees are payable in advance and in the currency specified at checkout. Refunds, where applicable, will be processed in accordance with our published refund policy. Gyansathi reserves the right to modify pricing at any time, with changes applicable to future enrollments only.',
  },
  {
    title: '8. Certifications',
    body: 'Certificates issued by Gyansathi are awarded upon successful completion of the applicable course requirements, including assessments where prescribed. Certificates are for personal achievement recognition and do not constitute a formal academic degree unless expressly stated.',
  },
  {
    title: '9. Intellectual Property',
    body: 'All content on the Gyansathi platform — including text, videos, graphics, logos, course materials, quizzes, and software — is the property of Gyansathi or its licensors and is protected by applicable intellectual property laws. You may not copy, reproduce, distribute, republish, or create derivative works from any content without prior written permission.',
  },
  {
    title: '10. User-Generated Content',
    body: 'By submitting any content (such as comments, assignments, or feedback) on the platform, you grant Gyansathi a non-exclusive, royalty-free, worldwide license to use, reproduce, and display that content for the purpose of operating and improving the services. You represent that you own or have the necessary rights to the content you submit.',
  },
  {
    title: '11. Prohibited Conduct',
    body: 'Users must not share account credentials with third parties, attempt to reverse-engineer the platform, scrape or harvest data, distribute malware, post unlawful or offensive content, harass other users, or engage in any activity that disrupts the integrity of the platform.',
  },
  {
    title: '12. Third-Party Services',
    body: 'The platform may integrate with or link to third-party services (such as payment gateways, video providers, or analytics tools). Gyansathi is not responsible for the availability, content, or practices of these third parties, and your use of them is governed by their own terms.',
  },
  {
    title: '13. Disclaimers',
    body: 'All educational content and guidance are provided for informational purposes only. Gyansathi does not guarantee specific academic, career, or employment outcomes. The platform is provided on an "as is" and "as available" basis, without warranties of any kind, whether express or implied.',
  },
  {
    title: '14. Limitation of Liability',
    body: 'To the maximum extent permitted by law, Gyansathi and its affiliates shall not be liable for any indirect, incidental, consequential, or punitive damages arising out of or related to your use of the platform. Our total liability for any claim shall not exceed the amount paid by you to Gyansathi in the twelve months preceding the claim.',
  },
  {
    title: '15. Suspension and Termination',
    body: 'Gyansathi reserves the right to suspend or terminate your access to the platform, with or without notice, if you breach these Terms, engage in fraudulent activity, or for any other reason at its sole discretion. Upon termination, your right to use the platform will cease immediately.',
  },
  {
    title: '16. Modifications to the Service',
    body: 'Gyansathi reserves the right to modify, suspend, or discontinue any part of the platform or its services at any time, with or without notice. We are not liable to you or any third party for any modification, suspension, or discontinuation of the services.',
  },
  {
    title: '17. Changes to These Terms',
    body: 'Gyansathi may update these Terms and Conditions from time to time. Updated versions will be posted on the website with a revised effective date. Your continued use of the platform after such changes constitutes acceptance of the revised Terms.',
  },
  {
    title: '18. Governing Law and Jurisdiction',
    body: 'These Terms and Conditions are governed by the laws of India, including the Information Technology Act, 2000. Any disputes arising out of or related to these Terms shall be subject to the exclusive jurisdiction of the competent courts in India.',
  },
  {
    title: '19. Privacy',
    body: 'Your use of the platform is also governed by our Privacy Policy, which explains how we collect, use, and protect your personal information. Please review the Privacy Policy carefully.',
  },
];

const TermsAndConditionsPage = () => {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-pastel-cream">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <button
              onClick={() => router.push('/')}
              className="flex items-center space-x-3"
            >
              <img
                src="/assets/logo-icon.png"
                alt="Gyan Sathi Logo"
                className="h-12 w-auto"
              />
              <div className="text-left">
                <span className="text-2xl font-bold text-pastel-navy">GYAN SATHI</span>
                <p className="text-xs text-gray-600 -mt-0.5">Smart Learning Platform</p>
              </div>
            </button>
            <button
              onClick={() => router.push('/')}
              className="hidden sm:inline-flex items-center space-x-2 px-5 py-2.5 bg-white border-2 border-pastel-blue-dark text-pastel-navy rounded-full hover:bg-pastel-blue/10 transition-all duration-300 text-sm font-semibold shadow-soft"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-pastel-mint/30 rounded-full blur-3xl" />
          <div className="absolute top-20 right-10 w-96 h-96 bg-pastel-blue/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-pastel-peach/30 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm border-2 border-pastel-yellow rounded-full px-5 py-2.5 shadow-soft mb-6">
            <ScrollText className="w-4 h-4 text-pastel-peach-dark" />
            <span className="text-sm font-semibold text-pastel-navy">Please Read Carefully</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-pastel-navy mb-4 leading-tight">
            Terms &amp; Conditions
          </h1>
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="h-1.5 w-20 bg-pastel-rose rounded-full" />
            <div className="h-1.5 w-12 bg-pastel-yellow rounded-full" />
            <div className="h-1.5 w-8 bg-pastel-mint rounded-full" />
          </div>
          <p className="text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
            These Terms and Conditions govern your access to and use of the Gyansathi platform. By using
            our website, mobile application, or any related services, you agree to abide by the terms set
            out below.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-soft-xl border border-gray-100 p-8 sm:p-12 space-y-8">
            {sections.map((section, index) => (
              <div key={index} className="group">
                <div className="flex items-start space-x-4">
                  <div className="hidden sm:flex w-10 h-10 bg-pastel-blue/30 rounded-xl items-center justify-center flex-shrink-0 mt-1">
                    <FileText className="w-5 h-5 text-pastel-blue-dark" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl sm:text-2xl font-bold text-pastel-navy mb-3">
                      {section.title}
                    </h2>
                    <p className="text-gray-700 leading-relaxed">{section.body}</p>
                  </div>
                </div>
                {index < sections.length - 1 && (
                  <div className="mt-8 border-b border-gray-100" />
                )}
              </div>
            ))}

            {/* Contact Card */}
            <div className="mt-10 bg-pastel-cream rounded-2xl p-6 sm:p-8 border-2 border-pastel-blue/30">
              <h2 className="text-2xl font-bold text-pastel-navy mb-4">20. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms and Conditions, please reach out to us:
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-soft">
                    <Mail className="w-5 h-5 text-pastel-blue-dark" />
                  </div>
                  <a
                    href="mailto:info@gyansathi.com"
                    className="text-pastel-navy font-semibold hover:text-pastel-blue-dark transition-colors"
                  >
                    info@gyansathi.com
                  </a>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-soft flex-shrink-0">
                    <MapPin className="w-5 h-5 text-pastel-blue-dark" />
                  </div>
                  <a
                    href="https://www.google.com/maps?q=28.946156,77.754158"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pastel-navy font-semibold hover:text-pastel-blue-dark transition-colors pt-2"
                  >
                    SF 66A, Somdutt City, A Pocket, Meerut
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-soft">
                    <Globe className="w-5 h-5 text-pastel-blue-dark" />
                  </div>
                  <Link
                    href="/contact-us"
                    className="text-pastel-navy font-semibold hover:text-pastel-blue-dark transition-colors"
                  >
                    Visit our Contact Us page
                  </Link>
                </div>
              </div>
              <p className="mt-6 text-sm text-gray-600">
                Also see our{' '}
                <Link
                  href="/privacy-policy"
                  className="text-pastel-blue-dark font-semibold hover:underline"
                >
                  Privacy Policy
                </Link>{' '}
                for details on how we handle your information.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-pastel-navy text-white py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img
              src="/assets/logo-icon.png"
              alt="Gyan Sathi Logo"
              className="h-10 w-auto"
            />
            <span className="text-xl font-bold">GYAN SATHI</span>
          </div>
          <p className="text-white/60 text-sm">
            &copy; 2026 Gyansathi. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditionsPage;
